import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import {
  IButtonDirective,
  IErrorComponent,
  IFormFieldComponent,
  IIconComponent,
  IInputDirective,
  ILabelComponent,
  ISelectMenuImports,
  MarketplaceFlagComponent,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { DataWarehouseAccountSelectionModalComponent } from "@m19-board/data-warehouse/data-warehouse-account-selection-modal/data-warehouse-account-selection-modal.component";
import { AccountMarketplace, DataWarehouseShare, GCloudLocation, Marketplace } from "@front/m19-api-client";
import { Marketplaces } from "@front/m19-models";
import { DataWarehouseService } from "@m19-board/services/data-warehouse.service";
import { AccountSelectionService } from "@front/m19-services";
import { map, tap } from "rxjs/operators";
import { toSignal } from "@angular/core/rxjs-interop";

export interface CreationForm {
  shareName: FormControl<string | null>;
  gcLocation: FormControl<GCloudLocation | null>;
  emails: FormArray<FormControl<string | null>>;
}

@Component({
  selector: "app-data-warehouse-edition",
  imports: [
    TranslocoDirective,
    ISelectMenuImports,
    IButtonDirective,
    FormsModule,
    ReactiveFormsModule,
    MarketplaceFlagComponent,
    IFormFieldComponent,
    IInputDirective,
    IErrorComponent,
    ILabelComponent,
    IIconComponent,
  ],
  templateUrl: "./data-warehouse-edition-modal.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataWarehouseEditionModalComponent implements OnInit {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "md" };

  private readonly modalService = inject(ModalService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalRef = inject(ModalRef);
  private readonly dataWarehouseService = inject(DataWarehouseService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toasterService = inject(ToasterService);
  private readonly updateData = inject<DataWarehouseShare>(MODAL_DATA);

  protected readonly isUpdate = this.updateData !== undefined;
  protected readonly SHARE_NAME_MAX_LENGTH = DataWarehouseService.SHARE_NAME_MAX_LENGTH;

  protected readonly editionForm = new FormGroup<CreationForm>({
    shareName: new FormControl<string>(
      { value: "", disabled: this.isUpdate },
      {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(45), Validators.pattern(/^[\p{Letter}0-9_\- ]+$/u)],
      },
    ),
    gcLocation: new FormControl<GCloudLocation | null>(null, { validators: [Validators.required] }),
    emails: new FormArray<FormControl<string | null>>([new FormControl(null, [Validators.required, Validators.email])]),
  });
  protected readonly topLocations: GCloudLocation[] = [
    GCloudLocation.EUROPE_WEST4,
    GCloudLocation.EU,
    GCloudLocation.US_WEST1,
    GCloudLocation.US,
  ];
  protected readonly gCloudLocations: GCloudLocation[] = [
    ...this.topLocations,
    ...Object.values(GCloudLocation)
      .filter((location) => !this.topLocations.includes(location))
      .sort((a, b) => a.localeCompare(b)),
  ];
  protected readonly searchQuery = signal("");
  protected readonly filteredGCloudLocations = computed(() => {
    const query = this.searchQuery().toLowerCase();
    return this.gCloudLocations.filter((location) => !query || location.toLowerCase().includes(query));
  });

  protected readonly Marketplaces = Marketplaces;

  protected loading = signal<boolean>(false);
  protected readonly selectedAccountMarketplaces = signal<AccountMarketplace[]>([]);
  protected hasAccountMarketplacesSelected = computed(() => this.selectedAccountMarketplaces().length > 0);
  protected selectedAccountMarketplaceGroups = computed(() => {
    const map = new Map<string, { accountId: string; accountName: string; marketplaces: Marketplace[] }>();

    for (const accountMarketplace of this.selectedAccountMarketplaces()) {
      if (!map.has(accountMarketplace.accountId)) {
        map.set(accountMarketplace.accountId, {
          accountId: accountMarketplace.accountId,
          accountName: accountMarketplace.accountName,
          marketplaces: [accountMarketplace.marketplace],
        });
      } else {
        map.get(accountMarketplace.accountId)!.marketplaces.push(accountMarketplace.marketplace);
      }
    }

    return Array.from(map.values());
  });
  protected readonly accountGroups = toSignal(
    this.accountSelectionService.selectedOrganizationAccountGroups$.pipe(map((org) => org.accountGroups)),
    { requireSync: true },
  );
  protected availableAccountMarketplaces = computed(() => {
    return this.accountGroups()
      .map((accountGroup) => accountGroup.getAccountMarketplaces())
      .flat();
  });

  ngOnInit(): void {
    if (this.isUpdate) {
      this.editionForm.controls.shareName.setValue(this.updateData.shareName);
      this.editionForm.controls.gcLocation.setValue(this.updateData.gcLocation);
      const accountMarketplaces: AccountMarketplace[] = this.updateData.resources.map((r) => ({
        accountId: r.accountId ?? "",
        marketplace: r.marketplace!,
        profileId: r.profileId,
        resourceOrganizationId: this.updateData.organizationId,
        accountName:
          this.availableAccountMarketplaces().find(
            (availableAccountMarketplace) => r.accountId === availableAccountMarketplace.accountId,
          )?.accountName ?? "",
      }));
      this.selectedAccountMarketplaces.set(accountMarketplaces);
      this.editionForm.controls.emails.clear();
      this.updateData.gcPrincipals.forEach((principal) => {
        this.editionForm.controls.emails.push(new FormControl(principal, [Validators.required, Validators.email]));
      });
    }
  }

  protected openAccountSelectionModal() {
    this.modalService
      .openModal<AccountMarketplace[], AccountMarketplace[]>(DataWarehouseAccountSelectionModalComponent, {
        size: "lg",
        data: this.selectedAccountMarketplaces(),
        modalTitle: this.translocoService.translate("data-warehouse-account-selection.title"),
        padding: "horizontal",
      })
      .afterClosed()
      .subscribe((selectedAccountMarketplaces) => {
        this.selectedAccountMarketplaces.set(selectedAccountMarketplaces);
      });
  }

  protected selectGCLocation(value: GCloudLocation | undefined | null) {
    if (value == null) return;
    this.editionForm.controls.gcLocation.setValue(value);
  }

  protected addGoogleCloudAccountLine() {
    this.editionForm.controls.emails.push(new FormControl(null, [Validators.required, Validators.email]));
  }

  protected removeGoogleCloudAccountLine(index: number) {
    this.editionForm.controls.emails.removeAt(index);
  }

  protected isValid() {
    return this.editionForm.valid && this.selectedAccountMarketplaces().length > 0;
  }

  protected cancel() {
    this.modalRef.close(false);
  }

  protected create() {
    this.loading.set(true);
    this.dataWarehouseService
      .createDataWarehouseShare(
        this.editionForm.controls.shareName.value!,
        Array.from(new Set(this.editionForm.controls.emails.value.map((a) => a ?? ""))),
        this.editionForm.controls.gcLocation.value!,
        this.selectedAccountMarketplaces().map((accountMarketplace) => ({
          profileId: accountMarketplace.profileId,
          accountId: accountMarketplace.accountId,
          marketplace: accountMarketplace.marketplace,
        })),
      )
      .pipe(
        tap({
          next: () =>
            this.toasterService.success(
              this.translocoService.translate("data-warehouse-edition.creation_success", [
                this.editionForm.controls.shareName.value,
              ]),
            ),
          error: (_) => {
            this.toasterService.error(
              this.translocoService.translate("data-warehouse-edition.creation_error", [
                this.editionForm.controls.shareName.value,
              ]),
            );
          },
        }),
      )
      .subscribe();

    this.loading.set(false);
    this.modalRef.close();
  }

  protected update() {
    this.loading.set(true);
    this.dataWarehouseService
      .updateDataWarehouseShare(
        this.updateData.shareId!,
        Array.from(new Set(this.editionForm.controls.emails.value.map((a) => a ?? ""))),
        this.selectedAccountMarketplaces().map((accountMarketplace) => ({
          profileId: accountMarketplace.profileId,
          accountId: accountMarketplace.accountId,
          marketplace: accountMarketplace.marketplace,
        })),
        this.updateData.deleted,
      )
      .pipe(
        tap({
          next: () =>
            this.toasterService.success(
              this.translocoService.translate("data-warehouse-edition.update_success", [
                this.editionForm.controls.shareName.value,
              ]),
            ),
          error: (_) => {
            this.toasterService.error(
              this.translocoService.translate("data-warehouse-edition.update_error", [
                this.editionForm.controls.shareName.value,
              ]),
            );
          },
        }),
      )
      .subscribe();

    this.loading.set(false);
    this.modalRef.close();
  }
}
